import React, { useRef, useState } from 'react';
import {
  useBi,
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { memberWebAppButtonClick } from '@wix/bi-logger-challenges-member-web/v2';
import { StepType } from '@wix/ambassador-online-programs-v3-step/types';
import {
  IconButton,
  Tag,
  Text,
  TextField,
  TextFieldTheme,
  TextPriority,
} from '@wix/wix-ui-tpa/cssVars';
import { Send, SparklesFilledSmall } from '@wix/wix-ui-icons-common';

import { Challenges } from '../../../../../../../editor/types/Experiments';
import { ButtonNames } from '../../../../../../../contexts/main/biInterfaces';
import { useSidebarLayoutBase } from '../../contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { sidebarLayoutDataHooks } from '../../sidebarLayoutDataHooks';
import { classes } from './AiTutorStepEntry.st.css';

// Bounds the free text a learner types that reaches BI analytics
export const BI_USER_ENTRY_MAX_LENGTH = 500;

const PRESETS = [
  {
    translationKey: 'live-site.ai-chat.step-entry.preset.explain-simply',
    buttonName: ButtonNames.AiTutorStepEntryPresetExplainSimply,
  },
  {
    translationKey: 'live-site.ai-chat.step-entry.preset.key-takeaways',
    buttonName: ButtonNames.AiTutorStepEntryPresetKeyTakeaways,
  },
  {
    translationKey: 'live-site.ai-chat.step-entry.preset.summarize',
    buttonName: ButtonNames.AiTutorStepEntryPresetSummarize,
  },
] as const;

export interface AiTutorStepEntryProps {
  stepId: string;
  stepType: StepType;
}

export const AiTutorStepEntry: React.FC<AiTutorStepEntryProps> = ({
  stepId,
  stepType,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const bi = useBi();
  const { isAiChatEnabled, pendingAiChatMessage, openAiChatWithMessage } =
    useSidebarLayoutBase();
  const [question, setQuestion] = useState('');
  const inputRef = useRef<TextField>(null);
  // Not useId: the editor renders this widget with a pre-18 React
  const titleId = `ai-tutor-step-entry-title-${stepId}`;

  const isStepTypeSupported =
    stepType === StepType.ARTICLE || stepType === StepType.VIDEO;

  if (
    !experiments.enabled(Challenges.aiTutorStepEntryPoints) ||
    !isAiChatEnabled ||
    !isStepTypeSupported
  ) {
    return null;
  }

  const isPending = pendingAiChatMessage !== null;
  const trimmedQuestion = question.trim();

  const reportClick = (buttonName: ButtonNames, userEntry?: string) => {
    void bi.report(
      memberWebAppButtonClick({ buttonName, step: stepId, userEntry }),
    );
  };

  const submitQuestion = () => {
    if (!trimmedQuestion || isPending) {
      return;
    }

    reportClick(
      ButtonNames.AiTutorStepEntryAsk,
      trimmedQuestion.slice(0, BI_USER_ENTRY_MAX_LENGTH),
    );
    openAiChatWithMessage(trimmedQuestion, 'typed');
    setQuestion('');
    // The send button disables once the field is empty; keep keyboard focus
    // on the field instead of letting it fall back to the page
    inputRef.current?.focus();
  };

  const submitPreset = (message: string, buttonName: ButtonNames) => {
    if (isPending) {
      return;
    }

    reportClick(buttonName);
    openAiChatWithMessage(message, 'preset');
  };

  const placeholder = t('live-site.ai-chat.step-entry.placeholder');

  return (
    <div
      className={classes.root}
      data-hook={sidebarLayoutDataHooks.aiTutorStepEntry()}
      role="group"
      aria-labelledby={titleId}
      aria-busy={isPending}
    >
      <Text
        id={titleId}
        className={classes.title}
        tagName="h4"
        priority={TextPriority.primary}
      >
        <SparklesFilledSmall aria-hidden="true" />
        {t('live-site.ai-chat.step-entry.title')}
      </Text>
      <div className={classes.controls}>
        {!isMobile && (
          <TextField
            className={classes.input}
            autoComplete="off"
            theme={TextFieldTheme.Box}
            data-hook={sidebarLayoutDataHooks.aiTutorStepEntryInput()}
            ref={inputRef}
            value={question}
            // Read-only rather than disabled, so a focused field keeps focus
            readOnly={isPending}
            placeholder={placeholder}
            aria-label={placeholder}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault();
                submitQuestion();
              }
            }}
            suffix={
              <IconButton
                data-hook={sidebarLayoutDataHooks.aiTutorStepEntrySendBtn()}
                aria-label={t('live-site.ai-chat.step-entry.send.aria-label')}
                disabled={!trimmedQuestion || isPending}
                icon={<Send />}
                onClick={submitQuestion}
              />
            }
          />
        )}
        <div
          className={classes.presets}
          role="group"
          aria-label={t('live-site.ai-chat.step-entry.presets.aria-label')}
        >
          {PRESETS.map(({ translationKey, buttonName }) => {
            const label = t(translationKey);

            return (
              <Tag
                key={translationKey}
                className={classes.preset}
                data-hook={sidebarLayoutDataHooks.aiTutorStepEntryPreset()}
                onClick={() => submitPreset(label, buttonName)}
              >
                {label}
              </Tag>
            );
          })}
        </div>
      </div>
    </div>
  );
};
